import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription, interval } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { Terminal, TerminalsService } from './terminals.service';

interface StatusPill {
  label: string;
  dot: string; // emoji indicator
  cls: string; // bootstrap text/bg class
}

@Component({
  selector: 'app-terminals',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './terminals.component.html',
  styleUrl: './terminals.component.scss',
})
export class TerminalsComponent implements OnInit, OnDestroy {
  private terminalsService = inject(TerminalsService);
  private toastr = inject(ToastrService);

  /** Live refresh cadence — matches the build spec (~30s). */
  private readonly POLL_INTERVAL_MS = 30_000;

  terminals: Terminal[] = [];
  stores: { id: string; name: string }[] = [];
  selectedStoreId = '';
  loading = false;
  /** Per-terminal in-flight guard for the enable/disable toggle. */
  togglingIds = new Set<string>();
  lastRefreshedAt: Date | null = null;

  private pollSub?: Subscription;

  ngOnInit(): void {
    this.loadStores();
    this.fetch();
    // Background poll — silent so the table doesn't flash a spinner.
    this.pollSub = interval(this.POLL_INTERVAL_MS).subscribe(() =>
      this.fetch(true),
    );
  }

  ngOnDestroy(): void {
    this.pollSub?.unsubscribe();
  }

  loadStores(): void {
    this.terminalsService.getStores().subscribe({
      next: (res: any) => {
        const data = res?.data || res || [];
        this.stores = data.map((s: any) => ({ id: s.id, name: s.name }));
      },
      error: () => {
        // Non-fatal — the store filter just stays empty.
        this.stores = [];
      },
    });
  }

  fetch(silent = false): void {
    if (!silent) this.loading = true;
    this.terminalsService
      .getTerminals(this.selectedStoreId || undefined)
      .subscribe({
        next: (res: any) => {
          this.terminals = res?.data || res || [];
          this.loading = false;
          this.lastRefreshedAt = new Date();
        },
        error: (err: any) => {
          console.error('Error fetching terminals:', err);
          this.loading = false;
          if (!silent) this.toastr.error('Failed to load terminals.');
        },
      });
  }

  onStoreFilterChange(): void {
    this.fetch();
  }

  /** Derives the status pill from remote status + online heartbeat. */
  statusPill(t: Terminal): StatusPill {
    if (t.status === 'disabled' || t.status === 'decommissioned') {
      return {
        label: t.status === 'disabled' ? 'Disabled' : 'Decommissioned',
        dot: '⚪',
        cls: 'text-secondary',
      };
    }
    return t.online
      ? { label: 'Online', dot: '🟢', cls: 'text-success' }
      : { label: 'Offline', dot: '🔴', cls: 'text-danger' };
  }

  isDayOpen(t: Terminal): boolean {
    return t.billingState === 'open';
  }

  /** Toggle a terminal between active and disabled. */
  toggleStatus(t: Terminal): void {
    if (t.status === 'decommissioned') {
      this.toastr.info('Decommissioned terminals cannot be re-enabled here.');
      return;
    }
    if (this.togglingIds.has(t.id)) return;

    const next: 'active' | 'disabled' =
      t.status === 'active' ? 'disabled' : 'active';
    this.togglingIds.add(t.id);

    this.terminalsService.updateStatus(t.id, next).subscribe({
      next: () => {
        t.status = next;
        this.togglingIds.delete(t.id);
        this.toastr.success(
          `${t.terminalName} ${next === 'active' ? 'enabled' : 'disabled'}.`,
        );
      },
      error: (err: any) => {
        console.error('Error updating terminal status:', err);
        this.togglingIds.delete(t.id);
        this.toastr.error('Failed to update terminal status.');
      },
    });
  }

  /** Human-readable "time since last heartbeat". */
  relativeTime(iso: string | null): string {
    if (!iso) return 'Never';
    const diffMs = Date.now() - new Date(iso).getTime();
    if (diffMs < 0) return 'Just now';
    const sec = Math.floor(diffMs / 1000);
    if (sec < 60) return `${sec}s ago`;
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const day = Math.floor(hr / 24);
    return `${day}d ago`;
  }

  trackById(_index: number, t: Terminal): string {
    return t.id;
  }
}
